import React, { useEffect, useState } from "react";
import { Row, Col, Card, Form, Button, Spinner, Alert } from "react-bootstrap";
import axios from "axios";
import { Link, useNavigate, useParams, useLocation } from "react-router-dom";
import SunEditor from "suneditor-react";
import "suneditor/dist/css/suneditor.min.css";
import PageLayout from "../../layouts/PageLayout";
import GalleryUpload from "../ProductCreator/UploadImages";

export default function ProductCreatorEditProductPage() {
    const navigate = useNavigate();
    const location = useLocation();
    const { id } = useParams();

    const [passedItem, setPassedItem] = useState(location.state?.item || null);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);

    const [categories, setCategories] = useState([]);
    const [brands, setBrands] = useState([]);
    const [stores, setStores] = useState([]);

    const [selectedParentCategory, setSelectedParentCategory] = useState("");
    const [availableSubCategories, setAvailableSubCategories] = useState([]);

    const [formData, setFormData] = useState({
        id: "",
        product_code: "",
        title: "",
        product_qty: 1,
        product_condition: "new",
        selling_style: "Fixed",
        store_id: "",
        brand_id: "",
        category_id: "",
        short_buying_price: "",
        buying_price: "",
        sell_price_dollar: "",
        shipping_charges_dollar: "",
        short_description: "",
        description: "",
    });

    const [mainImagePreview, setMainImagePreview] = useState(null);
    const [mainImage, setMainImage] = useState(null);
    const [firstImagePreview, setFirstImagePreview] = useState(null);
    const [existingImages, setExistingImages] = useState([]);

    const authHeaders = {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    };

    useEffect(() => {
        const loadData = async () => {
            try {
                setLoading(true);

                // Initial data lister ki bajaye product-creator endpoint se fetch karein agar available ho, ya lister se
                const initResponse = await axios.get(`${process.env.REACT_APP_API_URL}/product-creator/initial-data`, {
                    headers: authHeaders,
                }).catch(() => axios.get(`${process.env.REACT_APP_API_URL}/product-creator/initial-data`, { headers: authHeaders }));

                const resData = initResponse.data.data || initResponse.data;
                const fetchedCategories = resData.product_category || resData.product_categories || [];

                setCategories(fetchedCategories);
                setBrands(resData.brand || resData.brands || []);
                setStores(resData.store || resData.stores || []);

                let productData = passedItem;
                if (!productData && id) {
                    const prodResponse = await axios.get(`${process.env.REACT_APP_API_URL}/product-creator/product/${id}`, {
                        headers: authHeaders,
                    });
                    productData = prodResponse.data.product || prodResponse.data.data || prodResponse.data;
                    setPassedItem(productData);
                }

                if (productData) {
                    const catId = productData.category_id || productData.category?.id || "";

                    setFormData({
                        id: productData.id || "",
                        product_code: productData.product_code || "",
                        title: productData.title || "",
                        product_qty: productData.product_qty ?? 1,
                        product_condition: productData.product_condition || "new",
                        selling_style: productData.selling_style || "Fixed",
                        store_id: productData.store_id || productData.store?.id || "",
                        brand_id: productData.brand_id || productData.brand?.id || "",
                        category_id: catId,
                        short_buying_price: productData.short_buying_price || "",
                        buying_price: productData.buying_price || "",
                        sell_price_dollar: productData.sell_price_dollar ?? "",
                        shipping_charges_dollar: productData.shipping_charges_dollar || 0,
                        short_description: productData.short_description || "",
                        description: productData.description || "",
                    });

                    setFirstImagePreview(productData.card_img || productData.first_img || null);
                    setExistingImages(productData.images || []);

                    // Map sub-category to parent category for dropdowns
                    if (catId && fetchedCategories.length > 0) {
                        for (const cat of fetchedCategories) {
                            const foundChild = cat.child_categories?.find(
                                (child) => String(child.id) === String(catId)
                            );
                            if (foundChild) {
                                setSelectedParentCategory(String(cat.id));
                                setAvailableSubCategories(cat.child_categories || []);
                                break;
                            }
                        }
                    }
                }
            } catch (err) {
                console.error("Error loading data:", err);
                setError("Failed to load product or initial form data.");
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [id, passedItem]);

    const handleParentCategoryChange = (e) => {
        const parentId = e.target.value;
        setSelectedParentCategory(parentId);

        const selectedCat = categories.find((cat) => String(cat.id) === String(parentId));
        const children = selectedCat ? selectedCat.child_categories || [] : [];

        setAvailableSubCategories(children);
        setFormData((prev) => ({ ...prev, category_id: "" }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleShortDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, short_description: content }));
    };

    const handleDescriptionChange = (content) => {
        setFormData((prev) => ({ ...prev, description: content }));
    };

    const handleMainImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setMainImage(file);
        setMainImagePreview(URL.createObjectURL(file));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSuccess(null);
        setSaving(true);

        try {
            const submitData = new FormData();
            Object.keys(formData).forEach((key) => {
                if (formData[key] !== null && formData[key] !== undefined) {
                    submitData.append(key, formData[key]);
                }
            });

            if (mainImage) {
                submitData.append("card_img", mainImage); // Backend validation ke mutabiq 'card_img'
            }

            submitData.append("_method", "PUT");

            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/product-creator/products/${formData.id}`,
                submitData,
                {
                    headers: {
                        ...authHeaders,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (!response.data.success && !response.data.status) {
                setError("Failed to update product.");
                return;
            }

            setSuccess("Product updated successfully.");
            setTimeout(() => {
                navigate("/product-creator/products");
            }, 1000);
        } catch (err) {
            console.error("Update error:", err);
            if (err.response?.data?.errors) {
                const messages = Object.values(err.response.data.errors).flat().join(" ");
                setError(messages);
            } else {
                setError(err.response?.data?.message || "Failed to update product.");
            }
        } finally {
            setSaving(false);
        }
    };

    const editorButtonList = [
        ['undo', 'redo'],
        ['font', 'fontSize', 'formatBlock'],
        ['bold', 'underline', 'italic', 'strike'],
        ['removeFormat'],
        ['outdent', 'indent'],
        ['align', 'horizontalRule', 'list'],
        ['table', 'link', 'image'],
        ['fullScreen', 'codeView']
    ];

    if (loading) {
        return (
            <PageLayout>
                <div className="text-center py-5">
                    <Spinner animation="border" />
                    <p className="mt-3 text-muted">Loading product details...</p>
                </div>
            </PageLayout>
        );
    }

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <div className="mc-card mb-4">
                        <div className="mc-breadcrumb">
                            <h3 className="mc-breadcrumb-title">Edit Product</h3>
                            <ul className="mc-breadcrumb-list">
                                <li className="mc-breadcrumb-item">
                                    <Link to="#" className="mc-breadcrumb-link">Home</Link>
                                </li>
                                <li className="mc-breadcrumb-item">Products</li>
                                <li className="mc-breadcrumb-item">Edit Product</li>
                            </ul>
                        </div>
                    </div>
                </Col>

                <Col xl={12}>
                    {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
                    {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}
                </Col>

                <Col xl={12}>
                    <Form onSubmit={handleSubmit}>
                        {/* STORE & BRAND */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Store & Brand Assignment</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Store <span className="text-danger">*</span></Form.Label>
                                            <Form.Select name="store_id" value={formData.store_id} onChange={handleChange} required>
                                                <option value="">Select Store</option>
                                                {stores.map((s) => (
                                                    <option key={s.id} value={s.id}>{s.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Brand <span className="text-danger">*</span></Form.Label>
                                            <Form.Select name="brand_id" value={formData.brand_id} onChange={handleChange} required>
                                                <option value="">Select Brand</option>
                                                {brands.map((b) => (
                                                    <option key={b.id} value={b.id}>{b.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* CATEGORIES */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Categories Selection</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Parent Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select value={selectedParentCategory} onChange={handleParentCategoryChange}>
                                                <option value="">Select Parent Category</option>
                                                {categories.map((cat) => (
                                                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sub Category <span className="text-danger">*</span></Form.Label>
                                            <Form.Select
                                                name="category_id"
                                                value={formData.category_id}
                                                onChange={handleChange}
                                                disabled={!selectedParentCategory}
                                                required
                                            >
                                                <option value="">Select Sub Category</option>
                                                {availableSubCategories.map((sub) => (
                                                    <option key={sub.id} value={sub.id}>{sub.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* PRODUCT INFO & PRICING */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Product Information & Pricing</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Product Title <span className="text-danger">*</span></Form.Label>
                                            <Form.Control type="text" name="title" value={formData.title} onChange={handleChange} required />
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Condition</Form.Label>
                                            <Form.Select name="product_condition" value={formData.product_condition} onChange={handleChange}>
                                                <option value="new">New</option>
                                                <option value="used">Used</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Selling Style</Form.Label>
                                            <Form.Select name="selling_style" value={formData.selling_style} onChange={handleChange}>
                                                <option value="Fixed">Fixed</option>
                                                <option value="Auction">Auction</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={2}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Quantity</Form.Label>
                                            <Form.Control type="number" name="product_qty" value={formData.product_qty} onChange={handleChange} min="0" />
                                        </Form.Group>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Short Buying Price</Form.Label>
                                            <Form.Control type="text" name="short_buying_price" value={formData.short_buying_price} onChange={handleChange} />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Buying Price <span className="text-danger">*</span></Form.Label>
                                            <Form.Control type="number" step="0.01" name="buying_price" value={formData.buying_price} onChange={handleChange} required />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Sell Price ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control type="number" step="0.01" name="sell_price_dollar" value={formData.sell_price_dollar} onChange={handleChange} required />
                                        </Form.Group>
                                    </Col>
                                    <Col md={3}>
                                        <Form.Group className="mb-4">
                                            <Form.Label>Shipping Charges ($) <span className="text-danger">*</span></Form.Label>
                                            <Form.Control type="number" step="0.01" name="shipping_charges_dollar" value={formData.shipping_charges_dollar} onChange={handleChange} required />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* SHORT DETAILS */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Short Details</h5></Card.Header>
                            <Card.Body>
                                <SunEditor
                                    setContents={formData.short_description}
                                    onChange={handleShortDescriptionChange}
                                    setOptions={{ buttonList: editorButtonList, height: 180 }}
                                />
                            </Card.Body>
                        </Card>

                        {/* CARD / MAIN IMAGE */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Card Image (Main Image)</h5></Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={3}>
                                        {mainImagePreview ? (
                                            <img src={mainImagePreview} alt="Preview" style={{ width: "100%", height: "150px", objectFit: "contain" }} />
                                        ) : firstImagePreview ? (
                                            <img
                                                src={firstImagePreview.startsWith("http") ? firstImagePreview : `${process.env.REACT_APP_IMG_URL}/${firstImagePreview.replace(/^\//, "")}`}
                                                alt="Existing"
                                                style={{ width: "100%", height: "150px", objectFit: "contain" }}
                                            />
                                        ) : null}
                                    </Col>
                                    <Col md={9}>
                                        <Form.Control type="file" accept="image/*" onChange={handleMainImageChange} />
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>

                        {/* LONG DESCRIPTION */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Long Description</h5></Card.Header>
                            <Card.Body>
                                <SunEditor
                                    setContents={formData.description}
                                    onChange={handleDescriptionChange}
                                    setOptions={{ buttonList: editorButtonList, height: 300 }}
                                />
                            </Card.Body>
                        </Card>

                        {/* GALLERY UPLOAD */}
                        <Card className="mb-4 shadow-sm">
                            <Card.Header><h5 className="mb-0">Product Gallery</h5></Card.Header>
                            <Card.Body>
                                <GalleryUpload pId={formData.id} images={existingImages} />
                            </Card.Body>
                        </Card>

                        {/* SUBMIT BUTTONS */}
                        <Card className="mb-5 shadow-sm">
                            <Card.Body className="d-flex justify-content-end gap-2">
                                <Button variant="secondary" onClick={() => navigate(-1)} disabled={saving}>Cancel</Button>
                                <Button type="submit" variant="primary" disabled={saving}>
                                    {saving ? <Spinner size="sm" animation="border" /> : "Update Product"}
                                </Button>
                            </Card.Body>
                        </Card>
                    </Form>
                </Col>
            </Row>
        </PageLayout>
    );
}